import type {
  RemittanceClaimDisposition,
  RemittanceSource,
  RemittanceStatus,
} from '../../services/api/types.gen';

/**
 * Display strings for remittance status/source. These live outside
 * `RemittanceManager` because the manager imports `RemittanceTableToolbar`, so
 * the toolbar cannot import them back from the manager without a cycle. Both the
 * filter dropdowns and the rendered table cells read from here, keeping one
 * source of truth.
 */
export const STATUS_LABELS: Record<RemittanceStatus, string> = {
  saved: 'Saved',
  completed: 'Completed',
};

export const STATUS_VARIANTS: Record<RemittanceStatus, string> = {
  saved: 'text-amber-700 border-amber-300 bg-amber-50',
  completed: 'text-green-700 border-green-300 bg-green-50',
};

export const SOURCE_LABELS: Record<RemittanceSource, string> = {
  stedi_webhook: 'Received electronically',
  manual_upload: 'Manual Upload',
  manual_entry: 'Manual Entry',
};

export const DISPOSITION_LABELS: Record<RemittanceClaimDisposition, string> = {
  paid: 'Paid',
  denied: 'Denied',
  reversed: 'Reversed',
  other: 'Other',
};

export const DISPOSITION_VARIANTS: Record<RemittanceClaimDisposition, string> =
  {
    paid: 'text-green-700 border-green-300 bg-green-50',
    denied: 'text-red-700 border-red-300 bg-red-50',
    reversed: 'text-orange-700 border-orange-300 bg-orange-50',
    other: 'text-gray-700 border-gray-300 bg-gray-50',
  };

export const SOURCE_FILTER_OPTIONS = (
  Object.keys(SOURCE_LABELS) as RemittanceSource[]
).map(v => ({ value: v, label: SOURCE_LABELS[v] }));

/**
 * Reference text for one Remittance Advice Remark Code (RARC) such as `MA01`.
 *
 * The 835 carries MOA/MIA and LQ remark codes as bare codes, so no description
 * ever arrives with a remittance. The X12 text is the frontend's own reference
 * data (`rarcDescriptions.json`, generated by `scripts/generate_rarc_descriptions.py`)
 * and is loaded lazily by `useRemarkCodeDescriptions`; this module is imported
 * eagerly by the billing tables, so it must never import that JSON itself.
 *
 * `undefined` when the code is not on the list, so callers fall back to the
 * bare code. A biller can hand-key a code on the worksheet, so the match
 * ignores case and surrounding whitespace (`ma01`, ` N130 `). The key is
 * otherwise whatever string the payer sent, and the parsed JSON object has a
 * prototype, so an own-property check keeps `constructor` or `__proto__` from
 * resolving to an inherited value.
 */
export function remarkCodeDescription(
  descriptions: Readonly<Record<string, string>> | null | undefined,
  code: string
): string | undefined {
  if (!descriptions) return undefined;
  const key = code.trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(descriptions, key)
    ? descriptions[key]
    : undefined;
}
